import { useState, useEffect } from 'react';
import { Menu, Form, Input, Select, Button, Row, Col, Upload, Avatar, App as AntdApp, Alert, Tag, Spin } from 'antd';
import type { UploadFile } from 'antd';
import { User as UserIcon, Lock, Signature, Bell, Clock, UploadCloud } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';
import { useAuthStore } from '@/store/authStore';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { authService } from '@/services/authService';
import { userService } from '@/services/userService';  // V1.0.63
import { apiClient } from '@/services/apiClient';
import { GENDER_OPTIONS } from '@/utils/genderHelper';  // V1.0.48

dayjs.extend(relativeTime);
dayjs.locale('vi');
const App = AntdApp; // alias để SessionsTab dùng App.useApp()

export function ProfilePage() {
  const { user, updateUser } = useAuthStore();
  const [selected, setSelected] = useState('profile');
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const { message } = AntdApp.useApp();

  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);
  const [sigFileList, setSigFileList] = useState<UploadFile[]>([]);
  const [sigPreview, setSigPreview] = useState<string | null>(null);
  const [loadingSig, setLoadingSig] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(Date.now()); // cache buster
  // V1.0.63: Danh sách khoa user đang quản lý
  const [managedDepts, setManagedDepts] = useState<Array<{ id: string; code: string; name: string }>>([]);

  // V1.0.63: Load danh sách khoa quản lý
  useEffect(() => {
    if (!user?.id) return;
    userService.getManagedDepartments(user.id)
      .then(depts => setManagedDepts(depts))
      .catch(() => setManagedDepts([]));
  }, [user?.id]);

  // Load profile form khi user load
  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        username: user.username,
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        position: user.position,
        gender: user.gender ?? 'UNKNOWN',  // V1.0.48
      });
    }
  }, [user, profileForm]);

  // Load signature từ API khi vào tab signature
  useEffect(() => {
    if (selected !== 'signature' || !user?.id) return;

    setLoadingSig(true);
    // Gọi qua apiClient để có Bearer token
    apiClient
      .get(`/auth/signature/${user.id}`, { responseType: 'blob' })
      .then((resp) => {
        // Convert blob thành data URL để <img src> render được
        const reader = new FileReader();
        reader.onload = () => setSigPreview(reader.result as string);
        reader.readAsDataURL(resp.data);
      })
      .catch((e) => {
        // 404 = chưa có chữ ký, bình thường, không báo lỗi
        if (e.response?.status !== 404) {
          console.warn('[Profile] Load signature error:', e);
        }
        setSigPreview(null);
      })
      .finally(() => setLoadingSig(false));
  }, [selected, user?.id]);

  // --- HANDLERS ---

  const handleSaveProfile = async () => {
    try {
      const values = await profileForm.validateFields();
      setSavingProfile(true);
      const updated = await authService.updateProfile({
        fullName: values.fullName,
        email: values.email,
        phone: values.phone,
        position: values.position,
        gender: values.gender,  // V1.0.48
      });
      updateUser(updated);
      message.success('Đã lưu thông tin cá nhân');
    } catch (e: any) {
      console.error('[Profile] Save error:', e);
      message.error(e.response?.data?.message ?? e.message ?? 'Không lưu được');
    } finally { setSavingProfile(false); }
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error('Xác nhận mật khẩu không khớp');
        return;
      }
      if (values.newPassword.length < 8) {
        message.error('Mật khẩu mới phải từ 8 ký tự');
        return;
      }
      setChangingPwd(true);
      await authService.changePassword(values.oldPassword, values.newPassword);
      message.success('Đã đổi mật khẩu. Lần đăng nhập sau dùng mật khẩu mới.');
      passwordForm.resetFields();
    } catch (e: any) {
      console.error('[Profile] Change password error:', e);
      message.error(e.response?.data?.message ?? e.message ?? 'Đổi mật khẩu thất bại');
    } finally { setChangingPwd(false); }
  };

  const handleUploadSignature = async () => {
    if (sigFileList.length === 0) {
      message.warning('Chưa chọn file');
      return;
    }
    const file = sigFileList[0].originFileObj as File;
    if (!file) {
      message.error('File không hợp lệ');
      console.error('[Profile] originFileObj is null', sigFileList[0]);
      return;
    }
    console.log('[Profile] Uploading signature:', file.name, file.type, file.size + 'B');

    if (file.size > 2 * 1024 * 1024) {
      message.error('Ảnh vượt quá 2MB');
      return;
    }
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      message.error('Chỉ chấp nhận PNG/JPG');
      return;
    }
    setUploadingSig(true);
    try {
      const updated = await authService.uploadSignature(file);
      console.log('[Profile] Upload success, server returned:', updated);
      updateUser(updated);
      // Tạo preview từ file vừa upload (data URL)
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setSigPreview(dataUrl);
        console.log('[Profile] Preview set, data URL length:', dataUrl?.length);
      };
      reader.readAsDataURL(file);
      setSigFileList([]);
      message.success('Đã cập nhật chữ ký');
    } catch (e: any) {
      console.error('[Profile] Upload signature error:', e);
      console.error('[Profile] Response:', e.response?.data);
      const errMsg = e.response?.data?.message
        ?? (typeof e.response?.data === 'string' ? e.response.data : null)
        ?? e.message
        ?? 'Upload thất bại';
      message.error(errMsg);
    } finally { setUploadingSig(false); }
  };

  return (
    <>
      <PageHeader title="Hồ sơ & Cài đặt cá nhân" />

      <Row gutter={16}>
        <Col xs={24} md={6}>
          <Card padding={8}>
            <Menu
              mode="inline"
              selectedKeys={[selected]}
              onSelect={({ key }) => setSelected(key as string)}
              style={{ border: 0 }}
              items={[
                { key: 'profile', icon: <UserIcon size={14} />, label: 'Thông tin cá nhân' },
                { key: 'security', icon: <Lock size={14} />, label: 'Bảo mật & Mật khẩu' },
                { key: 'signature', icon: <Signature size={14} />, label: 'Chữ ký điện tử' },
                { key: 'notifications', icon: <Bell size={14} />, label: 'Thông báo' },
                { key: 'sessions', icon: <Clock size={14} />, label: 'Phiên hoạt động' },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} md={18}>
          {/* ======== PROFILE ======== */}
          {selected === 'profile' && (
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 0 }}>Thông tin cá nhân</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <Upload
                  name="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    // Validate size 2MB
                    if (file.size > 2 * 1024 * 1024) {
                      message.error('File không được vượt quá 2MB');
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    setUploadingAvatar(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file as File);
                      const resp = await apiClient.post('/auth/avatar', fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      message.success('Đã cập nhật ảnh đại diện');
                      // Refresh: update authStore + bust cache của avatar
                      setAvatarVersion(Date.now());
                      updateUser({ avatar: resp.data.avatarUrl });
                      onSuccess?.(resp.data);
                    } catch (e: any) {
                      message.error(e.response?.data?.message ?? 'Upload thất bại');
                      onError?.(e);
                    } finally {
                      setUploadingAvatar(false);
                    }
                  }}
                >
                  <div style={{ position: 'relative', cursor: 'pointer' }}>
                    {user?.id ? (
                      <Avatar
                        size={72}
                        src={`/api/auth/avatar/${user.id}?v=${avatarVersion}`}
                        style={{ background: '#e8f0fa', color: '#6699CC', fontSize: 24, fontWeight: 600 }}
                      >
                        {user?.fullName?.slice(0, 1)?.toUpperCase()}
                      </Avatar>
                    ) : (
                      <Avatar size={72} style={{ background: '#e8f0fa', color: '#6699CC', fontSize: 24, fontWeight: 600 }}>
                        {user?.fullName?.slice(0, 1)?.toUpperCase()}
                      </Avatar>
                    )}
                    {/* Overlay icon camera khi hover */}
                    <div style={{
                      position: 'absolute', bottom: 0, right: 0,
                      background: '#6699CC', color: '#fff',
                      width: 24, height: 24, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '2px solid #fff',
                      pointerEvents: 'none',
                    }}>
                      <UploadCloud size={12} />
                    </div>
                  </div>
                </Upload>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{user?.fullName}</div>
                  <div style={{ fontSize: 11, color: '#7d8a98' }}>{user?.department?.name}</div>
                  <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
                    {uploadingAvatar ? 'Đang upload...' : 'Bấm vào ảnh để thay đổi · PNG/JPG tối đa 2MB'}
                  </div>
                </div>
              </div>
              <Form form={profileForm} layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Họ và tên" name="fullName" rules={[{ required: true, message: 'Bắt buộc' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Tên đăng nhập" name="username">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Số điện thoại" name="phone">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Khoa/Phòng">
                      <Input value={user?.department?.name} disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Chức vụ" name="position">
                      <Input />
                    </Form.Item>
                  </Col>
                  {/* V1.0.63: Hiển thị danh sách khoa user quản lý (nếu có) */}
                  {managedDepts.length > 0 && (
                    <Col xs={24}>
                      <Form.Item label={`Khoa tôi quản lý (${managedDepts.length})`}
                        tooltip="Các khoa mà bạn là trưởng đơn vị. Bạn có quyền tương đương DEPT_HEAD cho các khoa này.">
                        <div style={{
                          padding: '8px 12px',
                          background: '#f0f5ff', border: '1px solid #adc6ff',
                          borderRadius: 6,
                          display: 'flex', flexWrap: 'wrap', gap: 8,
                        }}>
                          {managedDepts.map(d => (
                            <Tag key={d.id} color="blue" style={{ margin: 0, fontSize: 12, padding: '2px 8px' }}>
                              <b>{d.code}</b> · {d.name}
                            </Tag>
                          ))}
                        </div>
                      </Form.Item>
                    </Col>
                  )}
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Giới tính"
                      name="gender"
                      tooltip="Lưu thông tin giới tính cho mục đích thống kê. Hệ thống xưng hô chung là 'bạn'."
                    >
                      <Select options={GENDER_OPTIONS} placeholder="Chọn giới tính" />
                    </Form.Item>
                  </Col>
                </Row>
                <Button type="primary" onClick={handleSaveProfile} loading={savingProfile}>
                  Lưu thay đổi
                </Button>
              </Form>
            </Card>
          )}

          {/* ======== PASSWORD ======== */}
          {selected === 'security' && (
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 0 }}>🔐 Đổi mật khẩu</h3>
              <Alert
                message="Mật khẩu mới phải ít nhất 8 ký tự"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Form form={passwordForm} layout="vertical" style={{ maxWidth: 420 }}>
                <Form.Item label="Mật khẩu hiện tại" name="oldPassword" rules={[{ required: true }]}>
                  <Input.Password autoComplete="current-password" />
                </Form.Item>
                <Form.Item label="Mật khẩu mới" name="newPassword" rules={[{ required: true, min: 8 }]}>
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Form.Item label="Nhập lại mật khẩu mới" name="confirmPassword" rules={[{ required: true }]}>
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Button type="primary" onClick={handleChangePassword} loading={changingPwd}>
                  Đổi mật khẩu
                </Button>
              </Form>
            </Card>
          )}

          {/* ======== SIGNATURE ======== */}
          {selected === 'signature' && (
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 0 }}>✍ Chữ ký điện tử</h3>
              <p style={{ fontSize: 12, color: '#7d8a98' }}>
                Chữ ký sẽ được sử dụng khi ký các tài liệu, biểu mẫu trong hệ thống.
              </p>

              {/* BANNER HÀNH ĐỘNG - nổi bật, không thể miss */}
              {sigFileList.length > 0 && (
                <div style={{
                  background: '#e6f7ff',
                  border: '2px solid #6699CC',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0066cc' }}>
                      ✓ Đã chọn file: {sigFileList[0].name}
                    </div>
                    <div style={{ fontSize: 12, color: '#48535e', marginTop: 4 }}>
                      Bấm "LƯU CHỮ KÝ" bên phải để cập nhật chữ ký của bạn
                    </div>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<UploadCloud size={16} />}
                    style={{ height: 48, fontSize: 15, fontWeight: 700, minWidth: 200 }}
                    onClick={handleUploadSignature}
                    loading={uploadingSig}
                  >
                    LƯU CHỮ KÝ
                  </Button>
                </div>
              )}

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <div style={{ fontSize: 12, color: '#7d8a98', marginBottom: 6 }}>Chữ ký hiện tại</div>
                  <div
                    style={{
                      border: '1px solid #d4d9df',
                      borderRadius: 6,
                      height: 140,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#fff',
                      overflow: 'hidden',
                      padding: 8,
                    }}
                  >
                    {loadingSig ? (
                      <div style={{ color: '#7d8a98', fontSize: 12 }}>Đang tải...</div>
                    ) : sigPreview ? (
                      <img src={sigPreview} alt="Chữ ký"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#adb6c0', fontFamily: 'Brush Script MT,cursive', fontSize: 32, fontStyle: 'italic', opacity: 0.5 }}>
                          {user?.fullName?.split(' ').slice(-1)[0] ?? '...'}
                        </div>
                        <div style={{ fontSize: 10, color: '#adb6c0', marginTop: 4 }}>
                          Chưa có chữ ký · Upload để đăng ký
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div style={{ fontSize: 12, color: '#7d8a98', marginBottom: 6 }}>Tải lên chữ ký mới</div>
                  <Upload.Dragger
                    fileList={sigFileList}
                    beforeUpload={() => false}
                    maxCount={1}
                    accept="image/png,image/jpeg"
                    onChange={({ fileList }) => setSigFileList(fileList.slice(-1))}
                    onRemove={() => { setSigFileList([]); return true; }}
                    style={{ minHeight: 140 }}
                  >
                    <div style={{ padding: 20 }}>
                      <UploadCloud size={24} style={{ color: '#6699CC' }} />
                      <div style={{ fontSize: 12, marginTop: 8 }}>Kéo thả hoặc bấm để chọn</div>
                      <div style={{ fontSize: 10, color: '#adb6c0' }}>PNG nền trong suốt, 400×120 px, tối đa 2MB</div>
                    </div>
                  </Upload.Dragger>
                  <Button
                    type="primary"
                    size="large"
                    block
                    icon={<UploadCloud size={16} />}
                    style={{ marginTop: 12, height: 44, fontSize: 14, fontWeight: 600 }}
                    onClick={handleUploadSignature}
                    disabled={sigFileList.length === 0}
                    loading={uploadingSig}
                  >
                    {sigFileList.length === 0 ? 'Chọn file trước để lưu' : '💾 Lưu chữ ký lên hệ thống'}
                  </Button>
                  {sigFileList.length > 0 && (
                    <div style={{ fontSize: 11, color: '#52c41a', marginTop: 6, textAlign: 'center' }}>
                      ✓ Đã chọn: {sigFileList[0].name}
                    </div>
                  )}
                </Col>
              </Row>
            </Card>
          )}

          {/* ======== NOTIFICATIONS ======== */}
          {selected === 'notifications' && (
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 0 }}>🔔 Kênh thông báo</h3>
              <div style={{ padding: 20, textAlign: 'center', color: '#adb6c0' }}>
                Cấu hình thông báo chi tiết sẽ có trong phiên bản tiếp theo.
                <br />Hiện tại hệ thống gửi thông báo in-app và email cho tất cả sự kiện.
              </div>
            </Card>
          )}

          {/* ======== SESSIONS ======== */}
          {selected === 'sessions' && <SessionsTab />}
        </Col>
      </Row>
    </>
  );
}

// ========================================================
// Sessions Tab — phiên hoạt động của chính user
// ========================================================
function SessionsTab() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['my-sessions'],
    queryFn: async () => {
      const res = await apiClient.get('/auth/my-sessions');
      return res.data as Array<{
        id: string; ip?: string; userAgent?: string;
        createdAt: string; expiresAt?: string;
        revokedAt?: string; lastUsedAt?: string;
        isActive?: boolean;
      }>;
    },
  });

  // Đánh dấu phiên đầu tiên (mới nhất, chưa revoke) là "thiết bị hiện tại"
  // (list đã được backend sort theo created_at DESC)
  const currentSessionId = sessions.find(s => s.isActive && !s.revokedAt)?.id;

  const revokeMutation = useMutation({
    mutationFn: (sessionId: string) => apiClient.delete(`/users/sessions/${sessionId}`),
    onSuccess: () => {
      message.success('Đã thu hồi phiên');
      qc.invalidateQueries({ queryKey: ['my-sessions'] });
    },
    onError: (e: any) => message.error(e.response?.data?.message ?? 'Không thu hồi được'),
  });

  const parseBrowser = (ua?: string) => {
    if (!ua) return '-';
    if (ua.includes('Edg/')) return 'Microsoft Edge';
    if (ua.includes('Chrome/') && !ua.includes('Edg/')) return 'Google Chrome';
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    return ua.slice(0, 60);
  };
  const parseOS = (ua?: string) => {
    if (!ua) return '';
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('Linux')) return 'Linux';
    return '';
  };

  return (
    <Card>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 0, marginBottom: 12 }}>
        ⏱ Phiên hoạt động ({sessions.length})
      </h3>
      <p style={{ fontSize: 12, color: '#7d8a98', marginBottom: 16 }}>
        Danh sách các phiên đăng nhập gần đây từ các thiết bị. Nếu có phiên lạ, hãy thu hồi ngay.
      </p>

      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : sessions.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#adb6c0' }}>Chưa có phiên nào</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sessions.map((s) => {
            const browser = parseBrowser(s.userAgent);
            const os = parseOS(s.userAgent);
            const isRevoked = !!s.revokedAt;
            const expired = !isRevoked && s.expiresAt && new Date(s.expiresAt) < new Date();
            const isCurrent = s.id === currentSessionId;
            return (
              <div
                key={s.id}
                style={{
                  padding: 12,
                  border: '1px solid #eceef1',
                  borderRadius: 6,
                  background: (expired || isRevoked) ? '#f9fafb' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 500, fontSize: 13 }}>
                      {browser}{os ? ` · ${os}` : ''}
                    </span>
                    {isCurrent && <Tag color="success" style={{ margin: 0 }}>Thiết bị hiện tại</Tag>}
                    {isRevoked && <Tag color="error" style={{ margin: 0 }}>Đã thu hồi</Tag>}
                    {expired && !isRevoked && <Tag color="default" style={{ margin: 0 }}>Đã hết hạn</Tag>}
                  </div>
                  <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>
                    {s.ip && <>IP: <span className="mono">{s.ip}</span> · </>}
                    Đăng nhập: <span className="mono">{dayjs(s.createdAt).format('DD/MM/YYYY HH:mm')}</span>
                    {s.lastUsedAt && (
                      <> · Hoạt động cuối: <span className="mono">{dayjs(s.lastUsedAt).fromNow()}</span></>
                    )}
                  </div>
                </div>
                {!isCurrent && !isRevoked && !expired && (
                  <Button
                    danger
                    size="small"
                    loading={revokeMutation.isPending}
                    onClick={() => {
                      modal.confirm({
                        title: 'Thu hồi phiên này?',
                        content: 'Thiết bị đó sẽ bị đăng xuất và cần đăng nhập lại.',
                        okText: 'Thu hồi',
                        okButtonProps: { danger: true },
                        onOk: () => revokeMutation.mutate(s.id),
                      });
                    }}
                  >
                    Thu hồi
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
